<template>
  <artisio-modal size="xl" :title="modalTitle" @hidden="onHidden" :visible="show" modal-class="image-editor-modal"
                 @ok.prevent="onSubmit" :show-confirmation="false" body-class="p-0"
                 :ok-title="$t('Submit')" :is-loading="loading" :content-in-form="false" scrollable>
    <div class="d-flex outer-container">
      <div class="p-2 border-right">
        <div>
          <b-tooltip target="rotate-left" :title="$t('Rotate Left')" boundary="document" placement="right"/>
          <b-button variant="primary" @click.prevent="onRotateClick(-90)" id="rotate-left" class="mb-2">
            <font-awesome-icon icon="undo" class="icon"/>
          </b-button>
        </div>
        <div>
          <b-tooltip target="rotate-right" :title="$t('Rotate Right')" boundary="document" placement="right"/>
          <b-button variant="primary" @click.prevent="onRotateClick(90)" id="rotate-right">
            <font-awesome-icon icon="redo" class="icon"/>
          </b-button>
        </div>
      </div>
      <div class="flex-grow-1 p-3 d-flex align-items-center justify-content-center overflow-auto" ref="outerContainer">
        <div :show="!loading" class="image-container">
          <spinner :show="loading" height="30"/>
          <img :src="imgSrc" :style="imgStyle" @load="onImgLoad" ref="displayImg"/>
          <canvas hidden id="canvas" ref="canvas"></canvas>
        </div>
      </div>
    </div>
  </artisio-modal>
</template>

<script>
  import ArtisioModal from "@/core/components/artisio-modal/ArtisioModal";
  import {createNamespacedHelpers} from "vuex";
  import Spinner from "@/core/components/common/Spinner";
  import InventoryService from "@/modules/inventory/InventoryService";

  const {mapState: globalMapSate} = createNamespacedHelpers('global');

  export default {
    name: "ImageEditorModal",
    components: {Spinner, ArtisioModal},
    data() {
      return {
        show: false,
        loading: false,
        rotateDegree: 0,
        canvas: null,
        image: null,
        imageObj: null,
      }
    },
    computed: {
      ...globalMapSate(['imageFileFormats']),
      modalTitle() {
        return this.$i18n.t("Image Editor")
      },
      imgSrc() {
        if (!this.image) {
          return ''
        }
        return !this.image.data ? this.image.base64_src : this.image.data.original.url
      },
      imgStyle() {
        return `transform: rotate(${this.rotateDegree}deg)`
      }
    },
    methods: {
      onImgLoad() {
        let originalImgWidth = this.$refs.displayImg.width
        let originalImgHeight = this.$refs.displayImg.height

        if (this.$refs.displayImg.width > this.$refs.displayImg.height) {
          this.$refs.displayImg.width = Math.min(this.$refs.outerContainer.offsetWidth, this.$refs.outerContainer.offsetHeight)
          this.$refs.displayImg.height = originalImgHeight * this.$refs.displayImg.width / originalImgWidth
        } else {
          this.$refs.displayImg.height = Math.min(this.$refs.outerContainer.offsetWidth, this.$refs.outerContainer.offsetHeight)
          this.$refs.displayImg.width = originalImgWidth * this.$refs.displayImg.height / originalImgHeight
        }
      },
      showModal(image) {
        this.show = true
        this.image = image
        this.$nextTick(() => {
          this.canvas = this.$refs.canvas;
          this.drawImageInCanvas();
        })
      },
      hideModal() {
        this.show = false;
        this.rotateDegree = 0;
        this.canvas = null;
        this.image = null;
        this.imageObj = null;
      },
      onHidden() {
        this.rotateDegree = 0
        this.show = false
      },
      async onSubmit() {
        if (this.rotateDegree / 90 % 4 === 0) {
          // this means that nothing was change and there is no need to process any logic
          this.hideModal();
          return;
        }

        let imgData = this.canvas.toDataURL();
        let contentType = !this.image.data ? this.getBase64ContentType(this.imageObj.src) : this.image.data.original.content_type;
        let computedFileName = !this.image.data ? this.image.file.name : this.image.name;
        let file = await this.urlToFile(imgData, computedFileName, contentType)

        this.$emit('onSaveClick', {
          file: file,
          base64_src: imgData,
          blobPromise: InventoryService.convertBase64ToBlob(imgData),
          uuid: null,
          processing: false,
          to_be_deleted: false,
        });
        this.hideModal()
      },

      onRotateClick(deg) {
        if (deg === 90) {
          this.rotateDegree = (this.rotateDegree + 90) % 360;
        } else {
          if (this.rotateDegree === 0) {
            this.rotateDegree = 270
          } else {
            this.rotateDegree = (this.rotateDegree - 90) % 360;
          }
        }

        let ctx = this.canvas.getContext("2d");

        if (this.rotateDegree === 90 || this.rotateDegree === 270) {
          canvas.width = this.imageObj.height;
          canvas.height = this.imageObj.width;
        } else {
          canvas.width = this.imageObj.width;
          canvas.height = this.imageObj.height;
        }

        ctx.clearRect(0, 0, canvas.width, canvas.height);
        if (this.rotateDegree === 90 || this.rotateDegree === 270) {
          ctx.translate(this.imageObj.height / 2, this.imageObj.width / 2);
        } else {
          ctx.translate(this.imageObj.width / 2, this.imageObj.height / 2);
        }
        ctx.rotate(this.rotateDegree * Math.PI / 180);
        ctx.drawImage(this.imageObj, -this.imageObj.width / 2, -this.imageObj.height / 2);


        // return
        // this.rotateDegree += deg
        // let ctx = this.canvas.getContext("2d");
        // ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
        // ctx.save();
        // ctx.translate(this.canvas.width / 2, this.canvas.height / 2);
        // ctx.rotate(this.rotateDegree * Math.PI / 180);
        // ctx.drawImage(this.imageObj, -this.canvas.width / 2, -this.canvas.height / 2);
        // ctx.restore();
      },

      drawImageInCanvas() {
        this.imageObj = new Image();
        //Need to set crossOrigin attribute to solve canvas.toDataURL() SecurityError
        this.imageObj.crossOrigin = "Anonymous";
        this.loading = true

        this.imageObj.onload = () => {
          this.loading = false
          canvas.width = this.imageObj.width
          canvas.height = this.imageObj.height
          let ctx = this.canvas.getContext("2d");
          ctx.drawImage(this.imageObj, 50, 50)
        };

        this.imageObj.src = !this.image.data ? this.image.base64_src : this.image.data.original.url;
      },

      urlToFile(url, filename, mimeType) {
        return (fetch(url).then(function (res) {
            return res.arrayBuffer();
          }).then(function (buf) {
            return new File([buf], filename, {type: mimeType});
          })
        );
      },
      getBase64ContentType(str) {
        let base64Str = str.split(',')[0]
        let temp;
        this.imageFileFormats.forEach(format => {
          if (base64Str.includes(format)) {
            temp = format
          }
        })
        return temp
      }
    }
  }
</script>

<style lang="scss">
  .artisio-modal.image-editor-modal {
    .modal-dialog {
      max-width: calc(100vw - 5em) !important;

      .outer-container {
        max-height: 100%;

        .image-container {
          max-height: 100%;
          width: 100%;
          height: calc(100vh - 10em);
          display: flex;
          justify-content: center;
          align-items: center;
        }
      }
    }
  }
</style>
